"use client"

import { whatsappFloatingClicked } from "@eurodrive/utils/gtm"
import { LeasingCarDto, SaleCarDto } from "api"
import { usePathname } from "next/navigation"
import Script from "next/script"
import { FC, useEffect, useMemo, useState } from "react"

// Extend Window interface for WhatsApp widget
declare global {
  interface Window {
    onlineWaWidget?: {
      run: (options: any) => void
    }
  }
}

// Type guard to check if car is SaleCarDto
const isSaleCarDto = (car: SaleCarDto | LeasingCarDto): car is SaleCarDto => {
  return "vehicleNumber" in car
}

interface WhatsAppWidgetProps {
  carData?: SaleCarDto | LeasingCarDto | null
}

const WhatsAppWidget: FC<WhatsAppWidgetProps> = ({ carData }) => {
  const pathname = usePathname()
  const [isWidgetReady, setIsWidgetReady] = useState(false)

  // Build WhatsApp widget options dynamically
  const whatsAppOptions = useMemo(() => {
    const baseOptions = {
      baseUrl: "https://www.online.99digital.co.il",
      phoneNumber: "972547171721",
      startText: "אשמח לפרטים",
    }

    // Add car-specific data to startText if available
    if (carData) {
      const currentUrl =
        typeof window !== "undefined" ? window.location.href : ""
      const company =
        (carData as any).manufacturerNameForWebsite ||
        carData.manufacturerName ||
        ""
      const model =
        (carData as any).modelNameForWebsite || carData.modelName || ""
      let color = ""
      if (isSaleCarDto(carData)) {
        color = carData.colorDescription || ""
      } else if (carData.colors?.length) {
        // For leasing cars, include all available color descriptions
        color = carData.colors
          .map((c) => c.colorDescription)
          .filter(Boolean)
          .join(", ")
      }

      // Build message text with car details (semicolon separated with labels)
      const parts = [
        `אשמח לשוחח עם נציג לגבי הרכב הבא:`,
        `חברה: ${company}`,
        `דגם: ${model}`,
        color ? `צבע: ${color}` : null,
        currentUrl ? `קישור: ${currentUrl}` : null,
      ]

      const startText = parts.filter(Boolean).join("; ")

      return {
        ...baseOptions,
        startText,
      }
    }

    return baseOptions
  }, [carData, pathname])

  useEffect(() => {
    if (!isWidgetReady || typeof window === "undefined") {
      return
    }

    if (window.onlineWaWidget?.run) {
      window.onlineWaWidget.run(whatsAppOptions)
    }
  }, [isWidgetReady, whatsAppOptions])

  // Add click tracking for WhatsApp widget button
  useEffect(() => {
    const checkAndAttachListener = () => {
      // Look for WhatsApp widget button (adjust selector based on actual widget markup)
      const whatsappButton = document.querySelector(
        'a[href*="wa.me"], a[href*="whatsapp"], .whatsapp-widget, [class*="whatsapp"]',
      )

      if (whatsappButton) {
        const handleWhatsAppClick = () => {
          whatsappFloatingClicked("floating_widget")
        }

        whatsappButton.addEventListener("click", handleWhatsAppClick)

        return () => {
          whatsappButton.removeEventListener("click", handleWhatsAppClick)
        }
      }
    }

    // Try immediately
    const cleanup = checkAndAttachListener()

    // Also try after a delay in case widget loads later
    const timeoutId = setTimeout(() => {
      checkAndAttachListener()
    }, 2000)

    return () => {
      clearTimeout(timeoutId)
      if (cleanup) cleanup()
    }
  }, [])

  const handleWidgetScriptLoad = () => {
    setIsWidgetReady(true)
    if (typeof window !== "undefined" && window.onlineWaWidget?.run) {
      window.onlineWaWidget.run(whatsAppOptions)
    }
  }

  return (
    <Script
      id="whatsapp-widget"
      src="https://www.online.99digital.co.il/packs/js/wa_widget.js"
      strategy="afterInteractive"
      onLoad={handleWidgetScriptLoad}
    />
  )
}

export default WhatsAppWidget
